/**
 * Install global axios + fetch interceptors that capture the
 * `X-Request-Id` header from backend responses (issue #1446).
 *
 * The interceptors are passive — they record ids and continue. They do
 * NOT swallow errors, retry, or transform responses.
 */

import axios from "axios";

import { recordRequestId } from "./requestIdTracker";

const HEADER = "x-request-id";

let installed = false;

export function installRequestIdInterceptor(): void {
  if (installed) return;
  installed = true;

  axios.interceptors.response.use(
    (response) => {
      const id = response?.headers?.[HEADER];
      if (typeof id === "string" && id.length > 0) {
        recordRequestId(id, {
          url: response.config?.url,
          status: response.status,
        });
      }
      return response;
    },
    (error) => {
      const headers = error?.response?.headers;
      const id = headers?.[HEADER];
      if (typeof id === "string" && id.length > 0) {
        recordRequestId(id, {
          url: error.config?.url,
          status: error.response?.status,
        });
        // Surface the request id on the error itself so callers can include
        // it in toasts / log lines without having to scrape headers.
        error.requestId = id;
      }
      return Promise.reject(error);
    },
  );

  // Also capture ids from raw `fetch` calls (Playwright tests, third-party
  // libs that don't go through axios).
  if (typeof window !== "undefined" && typeof window.fetch === "function") {
    const original = window.fetch.bind(window);
    window.fetch = async (...args: Parameters<typeof fetch>) => {
      const response = await original(...args);
      try {
        const id = response.headers.get(HEADER);
        if (id) {
          const url =
            typeof args[0] === "string" ? args[0] : args[0] instanceof URL ? args[0].toString() : args[0]?.url;
          recordRequestId(id, { url, status: response.status });
        }
      } catch {
        /* never break the response on a tracking failure */
      }
      return response;
    };
  }
}
